import { useEffect, useState } from "react";
import { PRODUCT_VISUAL_GROUPS, PRODUCT_VISUAL_OPTIONS, type ProductVisualKey } from "@ventas/shared";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Sheet } from "../../components/Sheet";
import { Toast } from "../../components/Toast";
import { CheckIcon, PencilIcon, PlusIcon, TagIcon } from "../../components/icons";
import { useCategoryList, useCreateCategory, useUpdateCategory } from "./hooks";
import type { Category } from "./api";
import { CategoryGlyph } from "../../components/CatalogVisuals";

export function AdminCategoriesView() {
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Category | "new" | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const categories = useCategoryList(showInactive ? undefined : true);
  const update = useUpdateCategory();

  return (
    <main className="page page--narrow">
      <PageHeader
        title="Categorías"
        backHref="/more"
        actions={<Button variant="primary" onClick={() => setEditing("new")}><PlusIcon /> Nueva</Button>}
      />
      <section className="page-section">
        <div className="section-intro">
          <div><p className="eyebrow">Catálogo</p><h1>Organizá los productos</h1><p>Una única lista evita categorías duplicadas o mal escritas.</p></div>
          <label className="switch-row"><input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} /> Ver inactivas</label>
        </div>

        {categories.isLoading && <div className="skeleton-list"><span /><span /><span /></div>}
        {categories.data?.length === 0 && (
          <div className="empty-state"><TagIcon width={26} height={26} /><strong>Sin categorías</strong><span>Creá la primera para empezar a ordenar el catálogo.</span></div>
        )}
        <div className="entity-list">
          {categories.data?.map((category) => (
            <article className={`entity-row${category.active ? "" : " is-inactive"}`} key={category.id}>
              <span className="entity-row__icon"><CategoryGlyph name={category.name} iconKey={category.iconKey} width={28} height={28} /></span>
              <div className="entity-row__body"><strong>{category.name}</strong><small>{category._count.products} {category._count.products === 1 ? "producto" : "productos"}</small></div>
              <span className={`status-badge ${category.active ? "status-badge--success" : "status-badge--neutral"}`}>{category.active ? "Activa" : "Inactiva"}</span>
              <button className="icon-action" type="button" aria-label={`Editar ${category.name}`} onClick={() => setEditing(category)}><PencilIcon /></button>
              <button
                className="text-action"
                type="button"
                onClick={async () => {
                  await update.mutateAsync({ id: category.id, body: { active: !category.active } });
                  setFeedback(category.active ? "Categoría desactivada" : "Categoría activada");
                }}
              >
                {category.active ? "Desactivar" : "Activar"}
              </button>
            </article>
          ))}
        </div>
      </section>
      <CategorySheet target={editing} onClose={() => setEditing(null)} onSaved={(message) => { setEditing(null); setFeedback(message); }} />
      {feedback && <Toast kind="success" message={feedback} icon={<CheckIcon />} autoCloseMs={2400} onDismiss={() => setFeedback(null)} />}
    </main>
  );
}

function CategorySheet({ target, onClose, onSaved }: { target: Category | "new" | null; onClose: () => void; onSaved: (message: string) => void }) {
  const create = useCreateCategory();
  const update = useUpdateCategory();
  const [name, setName] = useState("");
  const [iconKey, setIconKey] = useState<ProductVisualKey>("package");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setName(target && target !== "new" ? target.name : "");
    setIconKey(target && target !== "new" ? target.iconKey : "package");
    setError(null);
  }, [target]);

  if (!target) return null;

  return (
    <Sheet open title={target === "new" ? "Nueva categoría" : "Editar categoría"} onClose={onClose}>
      <form className="form-stack" onSubmit={async (event) => {
        event.preventDefault();
        const nextName = name.trim();
        if (nextName.length < 2) return setError("Ingresá al menos 2 caracteres.");
        try {
          if (target === "new") await create.mutateAsync({ name: nextName, iconKey });
          else await update.mutateAsync({ id: target.id, body: { name: nextName, iconKey } });
          onSaved(target === "new" ? "Categoría creada" : "Categoría actualizada");
        } catch {
          setError("Ya existe una categoría con ese nombre o no se pudo guardar.");
        }
      }}>
        <Input label="Nombre" value={name} onChange={(event) => { setName(event.target.value); setError(null); }} autoFocus error={error ?? undefined} />
        <div className="category-icon-field">
          <label className="field-control">
            <span>Icono</span>
            <select value={iconKey} onChange={(event) => setIconKey(event.target.value as ProductVisualKey)}>
              {PRODUCT_VISUAL_GROUPS.map((group) => (
                <optgroup label={group} key={group}>
                  {PRODUCT_VISUAL_OPTIONS.filter((option) => option.group === group).map((option) => (
                    <option value={option.key} key={option.key}>{option.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <span className="category-icon-preview" aria-label="Vista previa del icono">
            <CategoryGlyph name={name} iconKey={iconKey} width={34} height={34} />
          </span>
        </div>
        <p className="field-help">Usá un nombre breve y general, por ejemplo “Indumentaria”.</p>
        <Button type="submit" fullWidth size="lg" disabled={create.isPending || update.isPending}>{create.isPending || update.isPending ? "Guardando…" : "Guardar categoría"}</Button>
      </form>
    </Sheet>
  );
}
